// src/pages/pmc/PmcRaBillsRegisterPane.js

import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const cx = (...xs) => xs.filter(Boolean).join(" ");
const toNum = (v) => Number(String(v || "").replace(/[^0-9.\-]/g, "")) || 0;

function Badge({ color = "gray", children }) {
  const map = {
    green: "bg-emerald-100 text-emerald-700 border-emerald-200",
    yellow: "bg-amber-100 text-amber-700 border-amber-200",
    red: "bg-red-100 text-red-700 border-red-200",
    blue: "bg-sky-100 text-sky-700 border-sky-200",
    gray: "bg-slate-100 text-slate-700 border-slate-200",
    dark: "bg-slate-900 text-white border-slate-900",
  };

  return (
    <span className={cx("inline-flex rounded-full border px-2.5 py-1 text-[10px] font-black", map[color] || map.gray)}>
      {children}
    </span>
  );
}

function StatCard({ label, value, note, tone = "emerald" }) {
  const map = {
    emerald: "from-emerald-500 to-teal-600",
    blue: "from-sky-500 to-blue-600",
    amber: "from-amber-400 to-orange-500",
    red: "from-red-500 to-rose-600",
    slate: "from-slate-700 to-slate-950",
  };

  return (
    <div className="rounded-2xl bg-white border border-emerald-100 shadow-sm overflow-hidden">
      <div className={cx("h-1 bg-gradient-to-r", map[tone] || map.emerald)} />
      <div className="p-4">
        <div className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{label}</div>
        <div className="mt-1 text-2xl font-black text-slate-900">{value}</div>
        {note && <div className="mt-1 text-[11px] text-slate-500">{note}</div>}
      </div>
    </div>
  );
}

export default function PmcRaBillsRegisterPane() {
  const [tenders, setTenders] = useState([]);
  const [raBills, setRaBills] = useState([]);

  const [loading, setLoading] = useState(true);
  const [selectedTenderId, setSelectedTenderId] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const [selectedBill, setSelectedBill] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [savingBill, setSavingBill] = useState(false);

  const loadAll = async () => {
    try {
      setLoading(true);

      const [tSnap, raSnap] = await Promise.all([
        getDocs(collection(db, "tenders")),
        getDocs(collection(db, "ra_bills")),
      ]);

      const tenderList = [];
      tSnap.forEach((d) => tenderList.push({ id: d.id, ...d.data() }));

      const tenderMap = {};
      tenderList.forEach((t) => {
        tenderMap[t.id] = t;
      });

      const billList = [];
      raSnap.forEach((d) => {
        const b = { id: d.id, ...d.data() };
        const tender = tenderMap[b.tenderId] || {};

        billList.push({
          ...b,
          _tender: tender,
          tenderTitle: b.tenderTitle || tender.title || tender.projectName || "",
          projectName: tender.title || tender.projectName || b.tenderTitle || "",
          pacsName: tender.ownerCompany || tender.pacsName || tender.pacsShortName || "",
          location: tender.city || tender.district || b.projectLocation || "",
          awardedVendorUid: tender.awardedVendorUid || "",
        });
      });

      billList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

      setTenders(tenderList);
      setRaBills(billList);
    } catch (e) {
      console.error(e);
      toast.error("RA bills load karte waqt error aya.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filteredBills = useMemo(() => {
    let list = [...raBills];

    if (selectedTenderId !== "all") {
      list = list.filter((b) => b.tenderId === selectedTenderId);
    }

    if (statusFilter !== "all") {
      if (statusFilter === "pending") {
        list = list.filter((b) => (b.status || "pending") !== "approved");
      } else {
        list = list.filter((b) => (b.status || "pending") === statusFilter);
      }
    }

    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter((b) => {
        const hay = [
          b.raNo,
          b.vendorName,
          b.vendorUid,
          b.tenderId,
          b.tenderTitle,
          b.projectName,
          b.pacsName,
          b.location,
          b.status,
        ]
          .map((x) => String(x || "").toLowerCase())
          .join("|");

        return hay.includes(needle);
      });
    }

    return list;
  }, [raBills, selectedTenderId, statusFilter, q]);

  const totals = useMemo(() => {
    const total = filteredBills.length;
    const pending = filteredBills.filter((b) => (b.status || "pending") !== "approved").length;
    const approved = filteredBills.filter((b) => b.status === "approved").length;
    const totalAmount = filteredBills.reduce((s, b) => s + toNum(b.totalAmount), 0);
    const approvedAmount = filteredBills
      .filter((b) => b.status === "approved")
      .reduce((s, b) => s + toNum(b.totalAmount), 0);

    return { total, pending, approved, totalAmount, approvedAmount };
  }, [filteredBills]);

  const selectedTender = useMemo(() => {
    if (selectedTenderId === "all") return null;
    return tenders.find((t) => t.id === selectedTenderId) || null;
  }, [tenders, selectedTenderId]);

  const openBill = async (bill) => {
    try {
      const sameVendorBills = raBills.filter(
        (b) => b.tenderId === bill.tenderId && b.vendorUid === bill.vendorUid
      );

      const boqSnap = await getDocs(collection(db, "tenders", bill.tenderId, "boq_items"));
      const boqMap = {};

      boqSnap.forEach((d) => {
        const data = d.data();
        boqMap[d.id] = toNum(data.boqQty || data.qtyTender || data.qty || 0);
      });

      const editable = (bill.items || []).map((it) => {
        const itemId = it.itemId || it.boqItemId || "";
        const boqQty = toNum(it.boqQty) || toNum(boqMap[itemId]) || 0;

        let executedOther = 0;

        sameVendorBills.forEach((b) => {
          if (b.id === bill.id) return;

          (b.items || []).forEach((oi) => {
            if ((oi.itemId || oi.boqItemId) === itemId) {
              executedOther += toNum(oi.qtyThisRa || oi.qty || 0);
            }
          });
        });

        const qtyThis = toNum(it.qtyThisRa || it.qty || 0);
        const maxThisBill = Math.max(boqQty - executedOther, 0);
        const rate = toNum(it.rate);

        return {
          itemId,
          itemNo: it.itemNo,
          description: it.description,
          unit: it.unit,
          boqQty,
          executedOther,
          maxThisBill,
          qtyThisRa: qtyThis,
          rate,
          amount: qtyThis * rate,
        };
      });

      setSelectedBill(bill);
      setEditItems(editable);
    } catch (e) {
      console.error(e);
      toast.error("RA bill detail load karte waqt error aya.");
    }
  };

  const handleQtyChange = (rowIdx, value) => {
    setEditItems((prev) =>
      prev.map((r, idx) => {
        if (idx !== rowIdx) return r;

        let v = toNum(value);

        if (v < 0) v = 0;

        if (v > r.maxThisBill) {
          toast.error(`Item ${r.itemNo}: Max allowed ${r.maxThisBill.toFixed(3)} ${r.unit}`);
          v = r.maxThisBill;
        }

        return {
          ...r,
          qtyThisRa: v,
          amount: v * r.rate,
        };
      })
    );
  };

  const editedTotal = useMemo(
    () => editItems.reduce((sum, r) => sum + toNum(r.amount), 0),
    [editItems]
  );

  const isLocked = selectedBill?.status === "approved";

  const saveChanges = async () => {
    if (!selectedBill) return;

    if (isLocked) {
      toast.error("Approved RA Bill edit nahi ho sakta.");
      return;
    }

    try {
      setSavingBill(true);

      const newItems = editItems.map((r) => ({
        itemId: r.itemId,
        itemNo: r.itemNo,
        description: r.description,
        unit: r.unit,
        boqQty: r.boqQty,
        rate: r.rate,
        qtyThisRa: r.qtyThisRa,
        executedPrev: r.executedOther,
        executedTotal: r.executedOther + r.qtyThisRa,
        amount: r.amount,
      }));

      await updateDoc(doc(db, "ra_bills", selectedBill.id), {
        items: newItems,
        totalAmount: editedTotal,
        updatedAt: serverTimestamp(),
      });

      const patch = {
        items: newItems,
        totalAmount: editedTotal,
      };

      setRaBills((prev) =>
        prev.map((b) => (b.id === selectedBill.id ? { ...b, ...patch } : b))
      );

      setSelectedBill((b) => (b ? { ...b, ...patch } : b));

      toast.success("RA Bill saved.");
    } catch (e) {
      console.error(e);
      toast.error("RA Bill save karte waqt error aya.");
    } finally {
      setSavingBill(false);
    }
  };
    const approveBill = async () => {
    if (!selectedBill) return;

    if (!editItems.length) {
      toast.error("No items to approve.");
      return;
    }

    if (isLocked) {
      toast.error("Ye RA Bill pehle hi approve ho chuka hai.");
      return;
    }

    try {
      setSavingBill(true);

      await updateDoc(doc(db, "ra_bills", selectedBill.id), {
        status: "approved",
        approvedAt: serverTimestamp(),
        locked: true,
        lockedAt: serverTimestamp(),
        finalAmount: editedTotal,
        updatedAt: serverTimestamp(),
      });

      const patch = {
        status: "approved",
        locked: true,
        finalAmount: editedTotal,
      };

      setRaBills((prev) =>
        prev.map((b) => (b.id === selectedBill.id ? { ...b, ...patch } : b))
      );

      setSelectedBill((b) => (b ? { ...b, ...patch } : b));

      toast.success("RA Bill approved & locked.");
    } catch (e) {
      console.error(e);
      toast.error("Approve karte waqt error aya.");
    } finally {
      setSavingBill(false);
    }
  };

  const deleteBill = async (bill) => {
    if ((bill.status || "pending") === "approved" || bill.locked) {
      toast.error("Approved / locked RA Bill delete nahi ho sakta.");
      return;
    }

    const ok = window.confirm(
      `RA Bill ${bill.raNo || ""} delete karna hai?\n\nStatus: ${
        bill.status || "pending"
      }`
    );

    if (!ok) return;

    try {
      await deleteDoc(doc(db, "ra_bills", bill.id));

      toast.success("RA Bill deleted.");

      setRaBills((prev) => prev.filter((b) => b.id !== bill.id));

      if (selectedBill?.id === bill.id) {
        setSelectedBill(null);
        setEditItems([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("RA Bill delete karte waqt error aya.");
    }
  };

  const downloadPdf = () => {
    if (!selectedBill) return;

    if (selectedBill.status !== "approved") {
      toast.error("PDF download se pehle RA Bill approve karo.");
      return;
    }

    try {
      generateRaBillPDF(selectedBill, editItems);
    } catch (e) {
      console.error(e);
      toast.error("PDF generate karte waqt error aya.");
    }
  };

  return (
    <div className="space-y-5">
      {/* HEADER */}
      <div className="rounded-3xl bg-gradient-to-r from-slate-950 via-emerald-950 to-teal-800 p-5 text-white shadow-xl">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.22em] text-emerald-300">
              Contractor RA Bill Control
            </div>

            <h2 className="mt-2 text-2xl font-black">
              Contractor RA Bills Register
            </h2>

            <p className="mt-1 text-sm text-emerald-50">
              All contractor RA bills directly yahan show honge — project select optional hai.
            </p>
          </div>

          <button
            type="button"
            onClick={loadAll}
            className="rounded-full bg-white px-4 py-2 text-xs font-black text-emerald-800 shadow hover:bg-emerald-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* SUMMARY */}
      <div className="grid gap-3 md:grid-cols-5">
        <StatCard
          label="RA Bills"
          value={totals.total}
          note="Filtered bills"
          tone="emerald"
        />

        <StatCard
          label="Pending"
          value={totals.pending}
          note="Approval pending"
          tone="amber"
        />

        <StatCard
          label="Approved"
          value={totals.approved}
          note="Locked RA bills"
          tone="blue"
        />

        <StatCard
          label="Total Amount"
          value={`₹ ${totals.totalAmount.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}`}
          note="Filtered total"
          tone="slate"
        />

        <StatCard
          label="Approved Amount"
          value={`₹ ${totals.approvedAmount.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })}`}
          note="Payment-ready"
          tone="emerald"
        />
      </div>

      {/* FILTERS */}
      <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-4 grid gap-3 md:grid-cols-3">
        <div>
          <div className="text-[11px] font-semibold text-slate-600">
            Project / Tender
          </div>

          <select
            value={selectedTenderId}
            onChange={(e) => {
              setSelectedTenderId(e.target.value);
              setSelectedBill(null);
              setEditItems([]);
            }}
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold"
          >
            <option value="all">All Projects</option>

            {tenders.map((t) => (
              <option key={t.id} value={t.id}>
                {(t.ownerCompany || t.pacsName || "PACS") +
                  " · " +
                  (t.title || t.projectName || "Untitled") +
                  " · " +
                  (t.city || t.district || "")}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="text-[11px] font-semibold text-slate-600">
            Status
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setSelectedBill(null);
              setEditItems([]);
            }}
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold"
          >
            <option value="all">All</option>
            <option value="pending">Pending / Editable</option>
            <option value="approved">Approved / Locked</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div>
          <div className="text-[11px] font-semibold text-slate-600">
            Search
          </div>

          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setSelectedBill(null);
              setEditItems([]);
            }}
            placeholder="RA No / Contractor / PACS / Tender..."
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold"
          />
        </div>
      </div>

      {selectedTender && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-[11px] text-slate-600">
          <b>Selected Project:</b>{" "}
          {selectedTender.title || selectedTender.projectName || "—"} ·{" "}
          {selectedTender.ownerCompany || selectedTender.pacsName || "—"} ·{" "}
          {selectedTender.city || selectedTender.district || "—"}
        </div>
      )}

      {/* RA LIST */}
      <div className="rounded-3xl border border-emerald-100 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-sm font-black text-emerald-800">
              RA Bills List
            </div>
            <div className="text-[11px] text-slate-500">
              Contractor submit kare to yahan automatically show hona chahiye.
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-center text-xs text-slate-500">
            Loading RA bills...
          </div>
        ) : filteredBills.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-500">
            No RA bills found. Agar contractor ne submit kiya hai to tenderId/vendorUid mismatch check karo.
          </div>
        ) : (
          <div className="max-h-[330px] overflow-auto rounded-2xl border border-slate-200">
            <table className="min-w-full text-[11px]">
              <thead className="sticky top-0 z-10 bg-slate-950 text-white">
                <tr>
                  <th className="px-3 py-3 text-left">RA No</th>
                  <th className="px-3 py-3 text-left">Project / PACS</th>
                  <th className="px-3 py-3 text-left">Contractor</th>
                  <th className="px-3 py-3 text-center">Date</th>
                  <th className="px-3 py-3 text-right">Amount</th>
                  <th className="px-3 py-3 text-center">Status</th>
                  <th className="px-3 py-3 text-center">Items</th>
                  <th className="px-3 py-3 text-center">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {filteredBills.map((r) => {
                  const locked = r.status === "approved" || r.locked;

                  return (
                    <tr
                      key={r.id}
                      className={cx(
                        "cursor-pointer hover:bg-emerald-50/70",
                        selectedBill?.id === r.id ? "bg-emerald-50" : "bg-white"
                      )}
                      onClick={() => openBill(r)}
                    >
                      <td className="px-3 py-3 align-top">
                        <div className="font-black text-slate-900">
                          {r.raNo || "—"}
                        </div>
                        <div className="font-mono text-[10px] text-slate-400">
                          {r.id}
                        </div>
                      </td>

                      <td className="px-3 py-3 align-top">
                        <div className="font-black text-slate-900">
                          {r.pacsName || "—"}
                        </div>
                        <div className="text-slate-600">
                          {r.projectName || r.tenderTitle || "—"}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {r.location || "—"} · {r.tenderId || "—"}
                        </div>
                      </td>

                      <td className="px-3 py-3 align-top">
                        <div className="font-semibold text-slate-800">
                          {r.vendorName || "—"}
                        </div>
                        <div className="font-mono text-[10px] text-slate-400">
                          {r.vendorUid || "—"}
                        </div>
                      </td>

                      <td className="px-3 py-3 text-center align-top">
                        {r.raDate || "—"}
                      </td>

                      <td className="px-3 py-3 text-right align-top font-black text-slate-900">
                        ₹{" "}
                        {Number(r.totalAmount || 0).toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        })}
                      </td>

                      <td className="px-3 py-3 text-center align-top">
                        {locked ? (
                          <Badge color="green">APPROVED / LOCKED</Badge>
                        ) : (r.status || "pending") === "rejected" ? (
                          <Badge color="red">REJECTED</Badge>
                        ) : (
                          <Badge color="yellow">PENDING</Badge>
                        )}
                      </td>

                      <td className="px-3 py-3 text-center align-top font-bold">
                        {Array.isArray(r.items) ? r.items.length : 0}
                      </td>

                      <td
                        className="px-3 py-3 text-center align-top"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => openBill(r)}
                            className="rounded-lg border px-3 py-1 text-[10px] font-bold hover:bg-emerald-50"
                          >
                            View / Edit
                          </button>

                          <button
                            type="button"
                            onClick={() => deleteBill(r)}
                            disabled={locked}
                            className={cx(
                              "rounded-lg px-3 py-1 text-[10px] font-bold",
                              locked
                                ? "bg-red-100 text-red-300 cursor-not-allowed"
                                : "bg-red-600 text-white hover:bg-red-700"
                            )}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* DETAIL */}
        {selectedBill && editItems.length > 0 && (
          <div className="mt-5 border-t border-slate-200 pt-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-sm font-black text-slate-900">
                  RA Bill Detail — {selectedBill.raNo || "—"}
                </div>

                <div className="text-[11px] text-slate-600">
                  Contractor: {selectedBill.vendorName || "—"} · Date:{" "}
                  {selectedBill.raDate || "—"} · Status:{" "}
                  {selectedBill.status || "pending"}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={downloadPdf}
                  className={cx(
                    "rounded-lg px-3 py-2 text-[11px] font-black",
                    selectedBill.status === "approved"
                      ? "bg-slate-900 text-white hover:bg-black"
                      : "bg-slate-200 text-slate-500 cursor-not-allowed"
                  )}
                >
                  Download PDF
                </button>

                <button
                  type="button"
                  onClick={approveBill}
                  disabled={savingBill || isLocked}
                  className={cx(
                    "rounded-lg px-3 py-2 text-[11px] font-black",
                    savingBill || isLocked
                      ? "bg-emerald-300 text-white cursor-not-allowed"
                      : "bg-emerald-600 text-white hover:bg-emerald-700"
                  )}
                >
                  {savingBill ? "Saving..." : "Approve & Lock"}
                </button>

                <button
                  type="button"
                  onClick={saveChanges}
                  disabled={savingBill || isLocked}
                  className={cx(
                    "rounded-lg border px-3 py-2 text-[11px] font-black",
                    savingBill || isLocked
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "hover:bg-emerald-50"
                  )}
                >
                  {savingBill ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>

            <div className="mt-4 max-h-[360px] overflow-auto rounded-2xl border border-slate-200">
              <table className="min-w-full text-[11px]">
                <thead className="sticky top-0 bg-slate-100">
                  <tr>
                    <th className="px-2 py-2 border">Item</th>
                    <th className="px-2 py-2 border text-left">Description</th>
                    <th className="px-2 py-2 border">Unit</th>
                    <th className="px-2 py-2 border text-right">BOQ Qty</th>
                    <th className="px-2 py-2 border text-right">Other RA</th>
                    <th className="px-2 py-2 border text-right">Max This RA</th>
                    <th className="px-2 py-2 border text-right">Rate</th>
                    <th className="px-2 py-2 border text-right">Qty This RA</th>
                    <th className="px-2 py-2 border text-right">Amount</th>
                  </tr>
                </thead>

                <tbody>
                  {editItems.map((r, idx) => (
                    <tr key={r.itemId + "-" + idx} className="odd:bg-white even:bg-slate-50">
                      <td className="px-2 py-1 border text-center">{r.itemNo}</td>
                      <td className="px-2 py-1 border">{r.description}</td>
                      <td className="px-2 py-1 border text-center">{r.unit}</td>
                      <td className="px-2 py-1 border text-right">{r.boqQty.toFixed(3)}</td>
                      <td className="px-2 py-1 border text-right">{r.executedOther.toFixed(3)}</td>
                      <td className="px-2 py-1 border text-right">{r.maxThisBill.toFixed(3)}</td>
                      <td className="px-2 py-1 border text-right">{r.rate.toFixed(2)}</td>
                      <td className="px-2 py-1 border text-right">
                        <input
                          type="number"
                          step="0.001"
                          value={r.qtyThisRa}
                          onChange={(e) => handleQtyChange(idx, e.target.value)}
                          disabled={isLocked}
                          className={cx(
                            "w-24 rounded-md border px-1 py-0.5 text-right",
                            isLocked ? "bg-slate-100" : "bg-white"
                          )}
                        />
                      </td>
                      <td className="px-2 py-1 border text-right">{r.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr className="bg-emerald-100 font-black">
                    <td colSpan={8} className="px-2 py-2 border text-right">
                      TOTAL
                    </td>
                    <td className="px-2 py-2 border text-right">
                      ₹{" "}
                      {editedTotal.toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------ PDF helper ------------ */
function generateRaBillPDF(bill, items) {
  const docPdf = new jsPDF("p", "mm", "a4");

  const pageWidth = docPdf.internal.pageSize.getWidth();
  const marginLeft = 15;
  const marginRight = 15;
  const contentWidth = pageWidth - marginLeft - marginRight;

  const title = bill.tenderTitle || bill.projectTitle || bill.projectName || "Running Account Bill";
  const contractor = bill.vendorName || "";
  const raNo = bill.raNo || "";
  const raDate = bill.raDate || dayjs().format("DD/MM/YYYY");
  const site = bill.projectLocation || bill.city || bill.district || bill.location || "";

  docPdf.setFont("helvetica", "bold");
  docPdf.setFontSize(14);
  docPdf.text("Running Account Bill", pageWidth / 2, 15, { align: "center" });

  docPdf.setFont("helvetica", "normal");
  docPdf.setFontSize(9);

  const titleLines = docPdf.splitTextToSize(title, contentWidth);
  docPdf.text(titleLines, marginLeft, 22);

  let y = 22 + titleLines.length * 4.5;
  y += 3;

  docPdf.text(`RA No: ${raNo}`, marginLeft, y);
  docPdf.text(`Date: ${raDate}`, pageWidth - marginRight, y, { align: "right" });

  y += 5;
  docPdf.text(`Contractor: ${contractor}`, marginLeft, y);

  if (site) {
    docPdf.text(`Site: ${site}`, pageWidth - marginRight, y, { align: "right" });
  }

  const body = (items || []).map((r, idx) => {
    const boq = r.boqQty || 0;
    const prevQty = r.executedOther || r.executedPrev || 0;
    const thisQty = r.qtyThisRa || r.qty || 0;
    const rate = r.rate || 0;
    const thisAmt = thisQty * rate;

    return [
      idx + 1,
      r.description,
      r.unit || "",
      boq.toFixed(3),
      prevQty.toFixed(3),
      thisQty.toFixed(3),
      rate.toFixed(2),
      thisAmt.toFixed(2),
    ];
  });

  const thisTotal = body.reduce((sum, row) => sum + Number(row[7] || 0), 0);

  autoTable(docPdf, {
    startY: y + 6,
    margin: { left: marginLeft, right: marginRight },
    head: [[
      "Sr",
      "Description",
      "Unit",
      "BOQ Qty",
      "Prev Qty",
      "This RA Qty",
      "Rate",
      "Amount",
    ]],
    body,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 1.5,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 8, halign: "center" },
      1: { cellWidth: 70 },
      2: { cellWidth: 10, halign: "center" },
      3: { cellWidth: 18, halign: "right" },
      4: { cellWidth: 18, halign: "right" },
      5: { cellWidth: 18, halign: "right" },
      6: { cellWidth: 16, halign: "right" },
      7: { cellWidth: 22, halign: "right" },
    },
  });

  const tableEndY = docPdf.lastAutoTable.finalY || 60;

  docPdf.setFont("helvetica", "bold");
  docPdf.setFontSize(9);
  docPdf.text(
    `TOTAL (This RA): Rs. ${thisTotal.toFixed(2)}`,
    pageWidth - marginRight,
    tableEndY + 10,
    { align: "right" }
  );

  const signY = tableEndY + 35;
  const sigWidth = (contentWidth - 20) / 3;

  const sig1X = marginLeft;
  const sig2X = marginLeft + sigWidth + 10;
  const sig3X = marginLeft + 2 * (sigWidth + 10);

  docPdf.setFont("helvetica", "normal");
  docPdf.setFontSize(9);

  docPdf.line(sig1X, signY, sig1X + sigWidth, signY);
  docPdf.line(sig2X, signY, sig2X + sigWidth, signY);
  docPdf.line(sig3X, signY, sig3X + sigWidth, signY);

  docPdf.text("Contractor / Agency", sig1X + sigWidth / 2, signY + 5, {
    align: "center",
  });
  docPdf.text("PMC Engineer", sig2X + sigWidth / 2, signY + 5, {
    align: "center",
  });
  docPdf.text("PMC CEO", sig3X + sigWidth / 2, signY + 5, {
    align: "center",
  });

  docPdf.save(`${raNo || "RA_Bill"}.pdf`);
}